import { z } from 'zod'
import { buildInfo } from '../generated/buildInfo'
import type { GardenPlan, StaticData } from '../types/domain'
import type { PhotoAnalysisResult } from './photoAnalysis'
import { stableStringify } from './inference/normalize'
import { gardenPlanSchema, labelForPlant, normalizeGardenPlan } from './planModel'
import type { PlannerOutputs } from './plannerDerived'

export const GARDEN_STATE_SCHEMA_VERSION = 'garden-state.v1'
export const GARDEN_SHARE_SCHEMA_VERSION = 'garden-share.v1'

export type GardenStateEnvelope = {
  schemaVersion: typeof GARDEN_STATE_SCHEMA_VERSION
  appVersion: string
  exportedAt: string
  plan: GardenPlan
}

const gardenStateEnvelopeSchema = z.object({
  schemaVersion: z.literal(GARDEN_STATE_SCHEMA_VERSION),
  appVersion: z.string(),
  exportedAt: z.string(),
  plan: gardenPlanSchema,
})

const gardenShareEnvelopeSchema = z.object({
  schemaVersion: z.literal(GARDEN_SHARE_SCHEMA_VERSION),
  plan: gardenPlanSchema,
})

export function createGardenStateEnvelope(plan: GardenPlan, exportedAt = new Date().toISOString()) {
  return gardenStateEnvelopeSchema.parse({
    schemaVersion: GARDEN_STATE_SCHEMA_VERSION,
    appVersion: buildInfo.version,
    exportedAt,
    plan: normalizeGardenPlan(plan),
  })
}

export function serializeGardenState(plan: GardenPlan) {
  return `${stableStringify(createGardenStateEnvelope(plan))}\n`
}

export function parseGardenState(value: unknown): GardenPlan {
  if (typeof value === 'string') {
    try {
      return parseGardenState(JSON.parse(value) as unknown)
    } catch {
      throw new Error('The selected file is not valid JSON.')
    }
  }
  const envelope = gardenStateEnvelopeSchema.safeParse(value)
  if (envelope.success) {
    return normalizeGardenPlan(envelope.data.plan)
  }
  return normalizeGardenPlan(value)
}

export function encodeShareHash(plan: GardenPlan) {
  const payload = stableStringify({
    schemaVersion: GARDEN_SHARE_SCHEMA_VERSION,
    plan: normalizeGardenPlan(plan),
  })
  return `garden=${base64UrlEncode(payload)}`
}

export function decodeShareHash(hash: string): GardenPlan | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const encoded = params.get('garden')
  if (!encoded) {
    return null
  }
  try {
    const payload = JSON.parse(base64UrlDecode(encoded)) as unknown
    const parsed = gardenShareEnvelopeSchema.parse(payload)
    return normalizeGardenPlan(parsed.plan)
  } catch {
    return null
  }
}

export function buildPlanSummary(plan: GardenPlan, data: StaticData, outputs: PlannerOutputs) {
  const crops = plan.selectedCropIds.map((id) => labelForPlant(data.plants, id)).join(', ') || 'none'
  const harvestTotal = outputs.harvests.reduce((sum, item) => sum + item.yieldKg, 0).toFixed(1)
  return [
    `${plan.name}`,
    `Crops: ${crops}`,
    `Frost zone: ${outputs.frostZone.label}`,
    `Soil: ${outputs.soil.label}, pH ${outputs.soil.ph}, ${outputs.soil.drainage} drainage`,
    `Sun: ${outputs.sunHours} weighted hours with ${plan.shadePercent}% shade`,
    `Watering: ${outputs.watering.litersPerWeek} L/week across ${outputs.watering.eventsPerWeek} events`,
    `Expected harvest: ${harvestTotal} kg`,
    `Generated by Complete Gardener Planner ${buildInfo.version} (${buildInfo.commit})`,
  ].join('\n')
}

export function buildHarvestCsv(outputs: PlannerOutputs) {
  const rows = [
    ['crop_name', 'plant_id', 'harvest_date', 'yield_kg', 'confidence'],
    ...outputs.harvests.map((item) => [
      item.cropName,
      item.plantId,
      item.harvestDateISO,
      item.yieldKg.toString(),
      item.confidence,
    ]),
  ]
  return `${rows.map((row) => row.map(csvCell).join(',')).join('\n')}\n`
}

export function serializeAnalysisResult(result: PhotoAnalysisResult) {
  return `${stableStringify(result)}\n`
}

export function safeFilename(value: string, suffix: string) {
  const base = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
  return `${base || 'garden-plan'}${suffix}`
}

function csvCell(value: string) {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function base64UrlEncode(value: string) {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function base64UrlDecode(value: string) {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=')
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}
